import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import DatatableFunc from "../../Common/DatatableFunc";
import Http from "../../Common/Http";
import IsLogin from "../../Common/IsLogin";
import ReactDataTable from "../../Common/ReactDataTable";

// Only these two see the production wise breakdown. Every other role keeps the
// columns it always had.
const PRODUCTION_WISE_ROLES = ["admin", "admin_viewer"];

const format = (value) => {
  const num = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
};

const ProductWiseStock = () => {
  const [productData, setProductData] = useState([]);
  const [productionStaff, setProductionStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [kitchenType, setKitchenType] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState("all");

  const showProductionWise = PRODUCTION_WISE_ROLES.includes(role);

  const productTypeOptions = [
    { value: "all", label: "All Product" },
    { value: "finished_goods", label: "Finished Good" },
    { value: "raw_materials", label: "Raw Material" },
    { value: "packing_materials", label: "Packing Material" },
  ];

  const fetchData = async () => {
    setLoading(true);
    const resRole = await Http.get(`/staff-type-check`);
    setRole(resRole.data);
    Http.get("/product-wise-stock?type=with_product_group_wise_stock")
      .then((res) => {
        if (res.data.status === "success") {
          setProductData(res.data.item);
          // Sent for admin / admin_viewer: the roster of productions the stock
          // is split across.
          setProductionStaff(
            Array.isArray(res.data.production_staff)
              ? res.data.production_staff
              : [],
          );
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching product stock data:", err);
        setLoading(false);
      });
    Http.get("/me")
      .then((res) => {
        setKitchenType(res.data.data.kitchen_type);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  };

  const filteredProductData = useMemo(() => {
    if (selectedProductType === "all") {
      return productData;
    }

    return productData.filter((item) => {
      const itemType = item?.type || item?.product_type;
      return itemType === selectedProductType;
    });
  }, [productData, selectedProductType]);

  // One column per production holding stock. The roster fixes the order; the
  // rows themselves are the fallback when no roster is sent.
  const productionCols = useMemo(() => {
    if (!showProductionWise) return [];

    const headers = new Map();

    productionStaff.forEach((staff) => {
      if (Number(staff.total_items) > 0) {
        const id = String(staff.production_id ?? staff.id);
        headers.set(id, staff.production_name || staff.name || `Production ${id}`);
      }
    });

    productData.forEach((item) => {
      (item?.productions || []).forEach((production) => {
        const id = String(production.production_id ?? production.id);
        if (!headers.has(id)) {
          headers.set(
            id,
            production.production_name || production.name || `Production ${id}`,
          );
        }
      });
    });

    return [...headers].map(([id, header]) => ({
      field: `production_qty_${id}`,
      header,
      sortable: false,
    }));
  }, [showProductionWise, productionStaff, productData]);

  // Each row carries its breakdown nested in `productions`; flatten it into the
  // `production_qty_<id>` fields the datatable renders.
  const tableData = useMemo(() => {
    if (!showProductionWise) return filteredProductData;

    return filteredProductData.map((item) => {
      const row = { ...item };

      productionCols.forEach((col) => {
        row[col.field] = "0.00";
      });

      (item.productions || []).forEach((production) => {
        const id = production.production_id ?? production.id;
        row[`production_qty_${id}`] = format(production.qty);
      });

      row.store_stock_qty = format(item.store_stock_qty);
      row.production_stock_qty = format(item.production_stock_qty);
      row.stock_qty = format(item.stock_qty);

      return row;
    });
  }, [showProductionWise, filteredProductData, productionCols]);

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const cols = [
    { field: "code", header: "Code" },
    { field: "name", header: "Name" },
    { field: "category_name", header: "Category" },
    { field: "unit_name", header: "Unit", sortable: false },
    ...(showProductionWise
      ? [
          { field: "store_stock_qty", header: "Store Stock", sortable: false },
          ...productionCols,
          {
            field: "production_stock_qty",
            header: "Production Stock",
            sortable: false,
          },
          { field: "stock_qty", header: "Total Stock", sortable: false },
        ]
      : [
          ...(role !== "admin" && role !== "kitchen" && role !== "kitchen_manager"
            ? [{ field: "stock_qty", header: "Current Stock", sortable: false }]
            : []),
          /*...(role === "admin" || role === "kitchen" || role === "kitchen_manager"
            ? (kitchenType === "ac"
                ? [{ field: "ac_conversion_qty", header: "Converted Qty", sortable: false }]
                : kitchenType === "nonac"
                  ? [{ field: "non_ac_conversion_qty", header: "Converted Qty", sortable: false }]
                  : kitchenType === "hotel_staff"
                    ? [{ field: "staff_conversion_qty", header: "Converted Qty", sortable: false }]
                    : kitchenType === "bus_staff"
                      ? [{ field: "others_conversion_qty", header: "Converted Qty", sortable: false }]
                      : [])
            : []),*/
          ...(role !== "production" && role!=='supply_chain'
            ? [{ field: "stock_qty", header: "Converted Qty", sortable: false }]
            : []),
        ]),

  ];

  return (
    <div>
      <IsLogin></IsLogin>
      {/* {loading && <LoaderComponent />} */}
      <div>
        <div className="am-mainpanel" style={{ marginTop: "50px" }}>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <h3 className="card-body-title mg-sm-b-30 text-center justify-content-center">
                Product Wise Current Stock
              </h3>
              <div className="col-md-4 col-lg-3 card pd-20 pd-sm-40">
                <label className="form-label">Filter by Product Type</label>
                <Select
                  className="basic-single"
                  classNamePrefix="select"
                  options={productTypeOptions}
                  value={productTypeOptions.find(
                    (option) => option.value === selectedProductType,
                  )}
                  onChange={(selectedOption) =>
                    setSelectedProductType(selectedOption?.value || "all")
                  }
                  isClearable={false}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 999 }) }}
                />
              </div>
              <hr />
              <div className="row mb-3">

              </div>
              <div className="card">
                <ReactDataTable
                  cols={cols}
                  receiveData={tableData}
                  isLoading={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductWiseStock;
